import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Check, X, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { useSubscription, type Plan } from "@/hooks/useSubscription";
import { supabase } from "@/integrations/supabase/client";
import { STRIPE_TIERS } from "@/lib/stripe-config";
import { usePageMeta } from "@/hooks/usePageMeta";
import { toast } from "@/hooks/use-toast";
import LandingNav from "@/components/landing/LandingNav";
import LandingFooter from "@/components/landing/LandingFooter";
import { PLAN_FEATURE_MATRIX as features } from "@/data/planFeatures";

export default function PricingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { plan: currentPlan, loading: subLoading } = useSubscription();
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);

  usePageMeta({
    title: "Pricing — Unranked",
    description: "Choose the right plan for your SEO needs. Free, Starter, or Growth.",
    canonical: "https://unrankedagency.com/pricing",
  });

  const handleCheckout = async (tier: "starter" | "growth") => {
    if (!user) {
      localStorage.setItem("pending_plan", tier);
      navigate("/signup");
      return;
    }
    setCheckoutLoading(tier);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { priceId: STRIPE_TIERS[tier].price_id },
      });
      if (error) throw error;
      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (err) {
      toast({ title: "Error", description: "Failed to start checkout. Please try again.", variant: "destructive" });
    } finally {
      setCheckoutLoading(null);
    }
  };

  const plans: Array<{
    key: Plan;
    name: string;
    price: string;
    originalPrice?: string;
    period: string;
    description: string;
    cta: string;
    popular?: boolean;
  }> = [
    {
      key: "free",
      name: "Free",
      price: "$0",
      period: "",
      description: "Try a single comprehensive audit — no signup needed.",
      cta: "Get Free Audit",
    },
    {
      key: "starter",
      name: "Starter",
      price: "$19",
      originalPrice: "$39",
      period: "/mo",
      description: "Unlock full insights with 10 audits per month.",
      cta: "Get Started",
      popular: true,
    },
    {
      key: "growth",
      name: "Growth",
      price: "$39",
      originalPrice: "$79",
      period: "/mo",
      description: "Everything you need to dominate search — unlimited.",
      cta: "Go Growth",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <LandingNav extraLinks={[{ label: "Pricing", href: "/pricing" }]} />

      <div className="container mx-auto px-6 py-20 max-w-5xl">
        <div className="text-center mb-16">
          <Badge variant="secondary" className="mb-4">
            <Sparkles className="w-3 h-3 mr-1" /> Simple Pricing
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            Choose Your <span className="gradient-text">Plan</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-xl mx-auto">
            Start free, upgrade when you're ready. Cancel anytime.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {plans.map((p) => {
            const isCurrentPlan = currentPlan === p.key && user;
            return (
              <Card
                key={p.key}
                className={`relative border-border bg-card ${p.popular ? "border-primary/50 shadow-[0_0_30px_-5px_hsl(160_84%_45%/0.15)]" : ""}`}
              >
                {p.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-primary text-primary-foreground">Most Popular</Badge>
                  </div>
                )}
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg">{p.name}</CardTitle>
                  <div className="flex items-baseline gap-1 mt-2">
                    {p.originalPrice && (
                      <span className="text-lg text-muted-foreground line-through mr-1">{p.originalPrice}</span>
                    )}
                    <span className="text-4xl font-bold">{p.price}</span>
                    {p.period && <span className="text-muted-foreground">{p.period}</span>}
                    {p.originalPrice && (
                      <Badge className="ml-2 bg-primary/10 text-primary border-primary/20 text-xs">
                        Save {Math.round((1 - parseInt(p.price.replace('$', '')) / parseInt(p.originalPrice.replace('$', ''))) * 100)}%
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">{p.description}</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  {isCurrentPlan ? (
                    <Button className="w-full" variant="outline" disabled>
                      Current Plan
                    </Button>
                  ) : p.key === "free" ? (
                    <Button className="w-full" variant="outline" onClick={() => navigate("/signup")}>
                      {p.cta}
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  ) : (
                    <Button
                      className={`w-full ${p.popular ? "glow-primary" : ""}`}
                      onClick={() => handleCheckout(p.key as "starter" | "growth")}
                      disabled={checkoutLoading === p.key}
                    >
                      {checkoutLoading === p.key ? "Loading…" : p.cta}
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}

                  <div className="space-y-2.5 pt-4 border-t border-border">
                    {features.map((f) => {
                      const value = f[p.key as keyof typeof f];
                      const isString = typeof value === "string";
                      const enabled = isString || value === true;
                      return (
                        <div key={f.label} className="flex items-center gap-2 text-sm">
                          {enabled ? (
                            <Check className="w-4 h-4 text-primary shrink-0" />
                          ) : (
                            <X className="w-4 h-4 text-muted-foreground/40 shrink-0" />
                          )}
                          <span className={enabled ? "text-foreground" : "text-muted-foreground/50"}>
                            {f.label}
                            {isString && <span className="text-muted-foreground ml-1">({value})</span>}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <p className="text-center text-sm text-muted-foreground mb-8">
          Questions before you buy? Email{" "}
          <a
            href="mailto:hello@unrankedagency.com"
            className="text-foreground hover:text-primary transition-colors underline underline-offset-4"
          >
            hello@unrankedagency.com
          </a>
        </p>
      </div>

      <LandingFooter />
    </div>
  );
}
